import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  TextField,
} from '@mui/material'
import PassengerSelectRows from 'components/Common/DisplayValues/PassengerSelectRows'
import { maskPhoneNumber } from 'components/Common/Phone/displayPhone'
import MuiPhoneForm from 'components/Common/Phone/muiPhoneForm'
import ConnectForm from 'components/RouteWizard/ConnectForm'
import Input from 'components/RouteWizard/Input'
import useClientUsers from 'hooks/useClientUsers'
import { useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'

export type Passenger = {
  isLeadPassenger: boolean
  userId?: number
  name: string
  contactNumber: string
  email: string
  userProperties?: any
}

type AdditionalPassengerDetailsProps = {
  clientId?: number
  namePrefix: string
  onRemove?: () => void
}

const AdditionalPassengerDetails = ({
  clientId,
  namePrefix,
  onRemove,
}: AdditionalPassengerDetailsProps) => {
  const { setValue, control } = useFormContext()
  const {
    fieldState: { error },
  } = useController({
    name: `${namePrefix}.name`,
    control,
  })
  const filter = createFilterOptions<any>()

  const { users } = useClientUsers(clientId)

  const options = useMemo<Passenger[]>(
    () =>
      users
        ?.filter((users: any) => users.lastName !== undefined)
        .sort((a: any, b: any) => (a.firstName > b.firstName ? 1 : -1))
        .map((u: any) => ({
          name:
            [u.firstName, u.lastName].filter(Boolean).join(' ') || 'NOT SET',
          email: u.email,
          isLeadPassenger: false,
          contactNumber: u.contact.mobileNumber,
          userId: u.id,
        })),
    [users],
  )

  const name = useWatch({ control, name: `${namePrefix}.name` })
  const userId = useWatch({ control, name: `${namePrefix}.userId` })

  if (clientId == null) {
    return null
  }

  const displayString = (option: Passenger) => {
    const name = option.name
    const email = option.email
      ? option.email
      : option.contactNumber
      ? maskPhoneNumber(option.contactNumber)
      : ''

    return PassengerSelectRows({
      main: name,
      description: email,
    })
  }

  return (
    <ConnectForm>
      <Autocomplete
        defaultValue={name}
        options={options}
        getOptionLabel={(user) => {
          if (user == null) {
            return ''
          }

          if (typeof user === 'string') {
            return user
          }

          if (user.inputValue) {
            return user.inputValue
          }

          return user.name
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params)
          console.log('additional passenger filtered', filtered)
          const { inputValue } = params
          // Suggest the creation of a new value
          const isExisting = options.some(
            (option) => inputValue === option.name,
          )
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              inputValue,
              name: `Add "${inputValue}"`,
            })
          }

          return filtered
        }}
        freeSolo
        id={`${namePrefix}-passenger-select`}
        onChange={(event, newValue) => {
          if (newValue && newValue.inputValue) {
            setValue(namePrefix, {
              isLeadPassenger: false,
              name: newValue.inputValue.trim(),
              userId: undefined,
            })
          } else if (typeof newValue === 'string') {
            setValue(namePrefix, {
              isLeadPassenger: false,
              name: newValue.trim(),
              userId: undefined,
            })
          } else if (newValue == null) {
            setValue(namePrefix, {
              isLeadPassenger: false,
              name: undefined,
              userId: undefined,
            })
          } else {
            setValue(namePrefix, {
              ...newValue,
              name: newValue.name
                ? newValue.name
                : [newValue.firstName, newValue.lastName]
                    .filter((n) => n != null)
                    .join(' '),
            } as Passenger)
          }
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.userId}>
            {displayString(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={'Passenger name'}
            error={Boolean(error?.message)}
            helperText={error?.message}
          />
        )}
        sx={{ flex: '1' }}
      />

      {name && !userId && (
        <>
          <MuiPhoneForm
            label="Contact number"
            name={`${namePrefix}.contactNumber`}
            fullWidth
          />
          <Input label="Email" type="email" name={`${namePrefix}.email`} />
        </>
      )}
      {onRemove && (
        <Button
          onClick={onRemove}
          variant="contained"
          color="error"
          sx={{
            alignSelf: 'flex-end',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#ffffff',
            backgroundColor: '#d99189',
            borderRadius: '6px',
            padding: '10px',
          }}
        >
          <CloseIcon sx={{ marginRight: '4px' }} />
          Remove
        </Button>
      )}
      <Box
        component="hr"
        sx={{ margin: '20px 0', borderColor: '#828282', opacity: '0.2' }}
      />
    </ConnectForm>
  )
}

export default AdditionalPassengerDetails
